import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { captureSmartTablePresetCreated } from "./analytics";
import {
  CreateModelComparisonPresetRequest,
  CreateModelComparisonPresetResponse,
  SmartTableDetail,
  unwrapSmartTableEntityPayload,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { routePaths } from "@/utils/routePaths";
import { displayToast } from "@/utils/toast";

export const useCreateModelComparisonPreset = ({ openInNewTab = false } = {}) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<SmartTableDetail, unknown, CreateModelComparisonPresetRequest>({
    mutationFn: async payload => {
      const response = await fetch(ENDPOINTS.smart_table_model_comparison_preset(wid), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });
      const responsePayload = (await handleResponse(
        response,
      )) as CreateModelComparisonPresetResponse;
      return unwrapSmartTableEntityPayload<SmartTableDetail>(responsePayload, "smart_table");
    },
    onSuccess: (smartTable, payload) => {
      queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, "list"] });

      captureSmartTablePresetCreated({
        workspaceId: wid,
        smartTableId: smartTable.id,
        presetType: "model-comparison",
        promptRegistryId: payload.prompt_registry_id,
        promptVersionNumber: payload.prompt_version_number,
      });

      if (workspaceId) {
        queryClient.invalidateQueries({ queryKey: ["folder-entities", workspaceId] });
        const url = routePaths.workspaceSmartTable(workspaceId, smartTable.id);
        if (openInNewTab) {
          window.open(url, "_blank");
        } else {
          navigate(url);
        }
      }

      displayToast("Comparison table created", ToastType.success);
    },
  });
};
